<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';

import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import DeliveryStatusBadge from './DeliveryStatusBadge.vue';
import {
  BaseTable,
  BaseTableRow,
  BaseTableCell,
} from 'dashboard/components-next/table';

const props = defineProps({
  deliveries: {
    type: Array,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
  noDataMessage: {
    type: String,
    default: '',
  },
});

const { t } = useI18n();
const route = useRoute();
const EMPTY_CELL_VALUE = '-';

const headers = computed(() => [
  t('CAMPAIGN.WHATSAPP.ANALYTICS.TABLE.CONTACT'),
  t('CAMPAIGN.WHATSAPP.ANALYTICS.TABLE.STATUS'),
  t('CAMPAIGN.WHATSAPP.ANALYTICS.TABLE.MESSAGE'),
  t('CAMPAIGN.WHATSAPP.ANALYTICS.TABLE.REASON'),
]);

const errorReason = delivery =>
  delivery.error_message || delivery.error_title || delivery.error_code || '';

const errorCode = delivery =>
  delivery.error_code &&
  String(delivery.error_code) !== String(errorReason(delivery))
    ? delivery.error_code
    : '';

const messageContent = delivery =>
  delivery.message_content ||
  t('CAMPAIGN.WHATSAPP.ANALYTICS.TABLE.MESSAGE_NOT_GENERATED');

const contactDetailsRoute = contactId => ({
  name: 'contacts_edit',
  params: {
    accountId: route.params.accountId,
    contactId,
  },
});

const isEmpty = computed(() => props.deliveries.length === 0);
</script>

<template>
  <div
    class="w-full overflow-hidden rounded-xl border border-auth-border bg-auth-surface text-auth-text dark:border-auth-border-dark dark:bg-auth-surface-dark dark:text-auth-text-dark"
  >
    <div
      class="flex flex-col gap-3 border-b border-auth-border px-5 py-4 dark:border-auth-border-dark sm:flex-row sm:items-center sm:justify-between sm:gap-4"
    >
      <span
        class="text-heading-2 font-semibold text-auth-text dark:text-auth-text-dark"
      >
        {{ t('CAMPAIGN.WHATSAPP.ANALYTICS.TABLE.TITLE') }}
      </span>
      <div class="min-w-0 p-1 -m-1 overflow-x-auto no-scrollbar">
        <slot name="filters" />
      </div>
    </div>
    <div
      v-if="loading"
      class="flex items-center justify-center py-20 text-auth-muted dark:text-auth-muted-dark"
    >
      <Spinner />
    </div>
    <div
      v-else
      class="overflow-x-auto [&_th:first-child]:ps-5 [&_td:first-child]:ps-5 [&_th:last-child]:pe-5 [&_td:last-child]:pe-5"
      :class="{ 'min-h-48': isEmpty }"
    >
      <BaseTable
        :headers="headers"
        :items="deliveries"
        :no-data-message="noDataMessage"
        class="[&_table]:!divide-auth-border dark:[&_table]:!divide-auth-border-dark [&_thead]:!border-auth-border [&_thead]:!bg-auth-soft dark:[&_thead]:!border-auth-border-dark dark:[&_thead]:!bg-auth-soft-dark [&_th]:!text-auth-muted dark:[&_th]:!text-auth-muted-dark [&_tbody]:!divide-auth-border dark:[&_tbody]:!divide-auth-border-dark [&_tbody]:!text-auth-muted dark:[&_tbody]:!text-auth-muted-dark [&_tbody_tr]:transition-colors [&_tbody_tr]:hover:bg-auth-soft/70 dark:[&_tbody_tr]:hover:bg-auth-soft-dark/70"
      >
        <template #row="{ items }">
          <BaseTableRow
            v-for="delivery in items"
            :key="delivery.contact.id"
            :item="delivery"
          >
            <template #default>
              <BaseTableCell>
                <div class="flex flex-col gap-0.5 py-1">
                  <RouterLink
                    :to="contactDetailsRoute(delivery.contact.id)"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="inline-flex w-fit max-w-full items-center gap-1 text-heading-3 text-auth-text underline-offset-2 transition-colors hover:text-auth-action hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-auth-action dark:text-auth-text-dark dark:hover:text-auth-accent dark:focus-visible:outline-auth-accent"
                    :title="t('CONTACTS_LAYOUT.CARD.VIEW_DETAILS')"
                  >
                    <span class="truncate">
                      {{ delivery.contact.name || '-' }}
                    </span>
                    <span
                      aria-hidden="true"
                      class="i-lucide-arrow-up-right size-3.5 shrink-0"
                    />
                  </RouterLink>
                  <span
                    class="truncate text-label-small tabular-nums text-auth-muted dark:text-auth-muted-dark"
                  >
                    {{ delivery.contact.phone_number || '-' }}
                  </span>
                </div>
              </BaseTableCell>
              <BaseTableCell>
                <DeliveryStatusBadge :status="delivery.status" />
              </BaseTableCell>
              <BaseTableCell>
                <span
                  class="line-clamp-2 block max-w-48 whitespace-pre-line text-body-main lg:max-w-md"
                  :class="
                    delivery.message_content
                      ? 'text-auth-muted dark:text-auth-muted-dark'
                      : 'italic text-auth-muted/70 dark:text-auth-muted-dark/70'
                  "
                >
                  {{ messageContent(delivery) }}
                </span>
              </BaseTableCell>
              <BaseTableCell>
                <div
                  v-if="errorReason(delivery)"
                  class="flex max-w-40 flex-col gap-0.5 lg:max-w-56"
                >
                  <span
                    class="line-clamp-2 text-body-main text-auth-muted dark:text-auth-muted-dark"
                  >
                    {{ errorReason(delivery) }}
                  </span>
                  <span
                    v-if="errorCode(delivery)"
                    class="text-label-small tabular-nums text-auth-muted/70 dark:text-auth-muted-dark/70"
                  >
                    {{
                      t('CAMPAIGN.WHATSAPP.ANALYTICS.TABLE.ERROR_CODE', {
                        code: errorCode(delivery),
                      })
                    }}
                  </span>
                </div>
                <span
                  v-else
                  class="text-body-main text-auth-muted/70 dark:text-auth-muted-dark/70"
                >
                  {{ EMPTY_CELL_VALUE }}
                </span>
              </BaseTableCell>
            </template>
          </BaseTableRow>
        </template>
      </BaseTable>
    </div>
    <slot name="footer" />
  </div>
</template>
